import { STAYS } from "./stays-data";

export const HOW_TO_BOOK_STEPS = [
  { num: "1", title: "Tell Us Your Idea", body: "Use the trip planner or send a journey request with dates, interests, and a rough budget." },
  { num: "2", title: "We Review Your Request", body: "Our trip team reviews availability and replies with next steps, a draft route where useful, and clear pricing." },
  { num: "3", title: "Refine Together", body: "Adjust the pace, stays, and stops with your planner until the journey feels right." },
  { num: "4", title: "Confirm & Go", body: "Approve the plan with our team, arrange payment offline, and we coordinate the confirmed arrangements." },
];

/** Reassurance strip for /how-to-book. Keep only platform-true process facts —
 *  never invent support SLAs, free date changes, or guaranteed departures. */
export const HOW_TO_BOOK_PROMISES = [
  { value: "Request", label: "Send a journey request — no online payment at this stage" },
  { value: "Human", label: "A real trip team reviews availability and next steps" },
  { value: "Offline", label: "Payment is arranged after arrangements are confirmed" },
  { value: "Open", label: "Scheduled dates are shown on each journey page" },
];

/**
 * Global How-to-Book FAQs appended to every tour detail FAQ list.
 * Keep these process-true. Do not reintroduce unverified promises such as
 * 24/7 support, free date changes, or guaranteed departures.
 */
export const HOW_TO_BOOK_FAQS = [
  {
    q: "When do I pay?",
    a: "No online payment is taken when you send a journey request. Our trip team confirms availability and final pricing by phone or WhatsApp, and payment is arranged after you approve the plan.",
  },
  {
    q: "Is there a booking fee?",
    a: "There is no separate fee to send a planning request or receive a quote on this site. You pay for the journey and services that are confirmed with your trip team.",
  },
  {
    q: "Can I change or cancel?",
    a: "If your plans change, contact our trip team. Any date changes or cancellations are reviewed according to the arrangements confirmed for your journey.",
  },
  {
    q: "How do payments work from abroad?",
    a: "After availability is confirmed, we arrange bank transfer or mobile wallet payment directly with you. Do not send card details over email or chat.",
  },
  {
    q: "What if I need help during the trip?",
    a: "Need help? Contact our trip team. For guided journeys, your planner can also put you in touch with the on-ground contact arranged for your trip.",
  },
  {
    q: "Are departures guaranteed?",
    a: "Available departure dates are shown on the journey page. Our team confirms the arrangements after reviewing your request — an OPEN date is not automatic confirmation that the departure is guaranteed.",
  },
];

// The "Stay" entries are computed from STAYS (below) rather than hand-typed -
// a hardcoded list here previously drifted out of sync as new properties
// were added to the real catalogue (6 of 15 properties were missing from
// this page as of the July 2026 client feedback round).
export const FIND_US_PLACES: {
  tag: string;
  name: string;
  address: string;
  rating?: string;
  image: string;
  href?: string;
}[] = [
  {
    tag: "Head Office",
    name: "Green Tourism Pakistan HQ",
    address: "Pine Hills Road, DHA Phase 5, Islamabad · Mon-Fri 9AM-5PM PKT",
    rating: "4.9",
    image: "/images/islamabad/green-tourism-hq.jpg",
  },
  ...STAYS.map((s) => ({
    tag: "Stay",
    name: s.name,
    address: s.secondaryGeography ?? s.region,
    rating: s.rating,
    image: s.image,
    href: `/stays/${s.slug}`,
  })),
];

export const PARTNERS_TYPES = [
  { icon: "🏔", title: "Stays & Hosts", body: "Guesthouses, hotels, and camps that care about where they sit. We bring guests who travel gently." },
  { icon: "🧭", title: "Guides & Operators", body: "Local guides and ground operators with deep regional knowledge and a safety-first record." },
  { icon: "🌍", title: "Agents & Resellers", body: "International travel agents who want a reliable, responsible Pakistan specialist on the ground." },
];

export const PARTNERS_STATS = [
  { value: "40+", label: "Local Partners Across 6 Regions" },
  { value: "100%", label: "Locally Hired Guides & Drivers" },
  { value: "2%", label: "Of Bookings to the GreenPak Fund" },
  { value: "1.2k", label: "Trips Delivered Together" },
];

export const CAREERS_PERKS = [
  { title: "Travel That Matters", body: "Field trips to the regions you'll represent, and a team that actually goes where it sends people." },
  { title: "Local & Remote", body: "An Islamabad HQ with field roles across the north, plus remote planning and content roles." },
  { title: "Real Impact", body: "Small team, big say. Your work shapes how thousands experience Pakistan responsibly." },
];

export const EVENTS_MONTHS = [
  { key: "all" as const, label: "All Year" },
  { key: "spring" as const, label: "Spring" },
  { key: "summer" as const, label: "Summer" },
  { key: "autumn" as const, label: "Autumn" },
  { key: "winter" as const, label: "Winter" },
];

/** @deprecated Use listEventIndexItems() from @/greenpak/events-index, kept for legacy imports */
export const EVENTS_ALL = [
  { month: "Mar-Apr", season: "spring" as const, slug: "spring-blossom", name: "Hunza Spring Blossom", blurb: "Apricot and cherry blossom across the valley.", region: "Gilgit-Baltistan" },
  { month: "Mar", season: "spring" as const, name: "Nowruz", blurb: "Persian new year, marked across the northern valleys.", region: "Gilgit-Baltistan" },
  { month: "May", season: "spring" as const, slug: "chilam-joshi", name: "Chilam Joshi", blurb: "The Kalash welcome spring in the Chitral valleys.", region: "KPK" },
  { month: "Jul", season: "summer" as const, slug: "shandur-polo", name: "Shandur Polo Festival", blurb: "Free-style polo on the world's highest ground at 3,700m.", region: "Chitral / GB" },
  { month: "Aug", season: "summer" as const, slug: "uchal", name: "Uchal Harvest", blurb: "Kalash harvest celebration of milk and cheese.", region: "KPK" },
  { month: "Sep", season: "autumn" as const, name: "Babusar & Autumn Drives", blurb: "Golden poplars and the last of the open high passes.", region: "KPK / GB" },
  { month: "Dec", season: "winter" as const, slug: "choimus", name: "Chaomos", blurb: "The Kalash winter solstice festival, their most sacred.", region: "KPK" },
];

export const PROMOTIONS_HERO = {
  badge: "SAVE 20%",
  title: "Hunza Spring Blossom Early-Bird",
  blurb: "Book the March-April blossom departures and save 20% on our 7-day Fairy Meadows & Hunza journey.",
  price: "$1,190",
  was: "$1,490",
  image: "/images/spring-blossom-hunza.jpg",
  href: "/tours/adventures-of-fairy-meadows-hunza",
};

/**
 * The three October 2026 posters from the tours team, as offer cards. Every
 * figure is the poster's; the pictures are GreenPak's own photographs, never
 * the posters' (owner, 5 Oct 2026). Also added to the saved Offers page by
 * scripts/add-autumn-winter-offers.ts, since an edited page ignores these
 * code defaults.
 */
export const AUTUMN_WINTER_DEALS = [
  {
    badge: "Tailor-made",
    expires: "Autumn",
    region: "Northern Pakistan",
    title: "Customized Autumn Tours",
    blurb: "You plan, we design, you explore: a custom itinerary, hotels of your choice, by road or air, for families and groups, with local support.",
    price: "Custom quote",
    was: "",
    image: "/images/Fairy Meadows and Nanga Parbat Base Camp change colors brilliantly in the autumn.jpg",
    href: "/plan",
  },
  {
    badge: "Early bird",
    expires: "Book by 2 Nov",
    region: "Gilgit-Baltistan",
    title: "Autumn Beauty of Northern Pakistan",
    blurb: "Five days by road to Attabad Lake, Baltit Fort, the Hussaini bridge, Khunjerab Pass and Naltar, departing 14 November.",
    price: "PKR 65,000",
    was: "PKR 69,000",
    image: "/images/licensed/attabad.jpg",
    href: "/tours/autumn-beauty-of-northern-pakistan",
  },
  {
    badge: "Festival",
    expires: "Chawmos",
    region: "Chitral & Kalash valleys",
    title: "Chawmos Winter Festival Tour",
    blurb: "Five days from Islamabad to the Kalash valleys for the winter festival. Quad sharing per person; twin sharing PKR 52,500.",
    price: "PKR 46,000",
    was: "",
    image: "/images/licensed/chilam-joshi-festival-bumburet-kalash-women.jpg",
    href: "/tours/chawmos-winter-festival-tour",
  },
];

export const PROMOTIONS_DEALS = [
  ...AUTUMN_WINTER_DEALS,
  { badge: "Free night", expires: "Select dates", region: "Khyber Pakhtunkhwa", title: "Stay 4, Pay 3 in Naran", blurb: "A free fourth night at GreenPak Riverfront Naran when you book four.", price: "$285", was: "$380", image: "/images/riverfront-naran.jpg" },
  { badge: "Festival", expires: "July", region: "Chitral / Gilgit-Baltistan", title: "Shandur Polo Package", blurb: "Festival access, transport, and camp stay included.", price: "$1,420", was: "$1,680", image: "/images/shandur-polo.jpg" },
  { badge: "Save 15%", expires: "Shoulder season", region: "Balochistan", title: "Makran Coast Escape", blurb: "Four days of empty beaches and Hingol badlands.", price: "$1,140", was: "$1,340", image: "/images/kund-malir-beach.jpg" },
  { badge: "Save 10%", expires: "Shoulder season", region: "Khyber Pakhtunkhwa", title: "Kaghan & Naran Short Break", blurb: "Lake Saiful Malook and the Babusar road.", price: "$700", was: "$780", image: "/images/licensed/lake-saifulmalook.jpg" },
  { badge: "Honeymoon", expires: "Year-round", region: "Gilgit-Baltistan", title: "Private Hunza for Two", blurb: "A tailormade six-day escape with upgrades.", price: "$1,990", was: "$2,200", image: "/images/hunza-valley-rakaposhi.webp" },
  { badge: "Group deal", expires: "Min 4 guests", region: "Punjab", title: "Lahore Heritage Weekend", blurb: "Forts, mosques, and the walled city by night.", price: "$540", was: "$640", image: "/images/badshahi-mosque.jpg" },
];

export const INSIGHTS_GUIDES = [
  { topic: "Annual report", slug: "2026", title: "Pakistan Insights 2026", read: "Annual report", image: "/images/tours/vibrant-landscape-of-hunza-valley.webp" },
  { topic: "Annual report", slug: "2025", title: "Pakistan Insights 2025", read: "Annual report", image: "/images/licensed/fairy-meadows-and-the-view-of-nanga-parbat.jpg" },
];

export const FIELD_GUIDE = {
  slug: "attabad-lake",
  name: "Attabad Lake",
  type: "Lake",
  heroImage: "/images/attabad-lake.jpg",
  tagline: "A startling turquoise lake on the Karakoram Highway, born from disaster, now the north's most photographed water.",
  intro: [
    "In January 2010 a massive landslide at Attabad dammed the Hunza River, submerging villages and a stretch of the Karakoram Highway. What the disaster left behind is astonishing: a 21-kilometre lake of deep, mineral turquoise, ringed by bare cliffs and snow peaks.",
    "Today the highway crosses on smooth Chinese-built bridges and through tunnels, and the lake has become a destination in its own right, for boat rides, jet-skis, lakeside cafés, and simply the colour of the water against the rock.",
  ],
  things: [
    "Boat ride across the lake",
    "Lakeside café & chai",
    "Jet-ski & kayak (summer)",
    "Photography at golden hour",
    "Drive the KKH tunnels",
    "Day trip from Karimabad",
  ],
  gallery: ["/images/attabad-lake.jpg", "/images/hunza-valley-rakaposhi.webp", "/images/khunjerab-pass.jpg"],
  facts: [
    { label: "Type", value: "Lake" },
    { label: "Region", value: "Hunza, GB" },
    { label: "Elevation", value: "~2,400m" },
    { label: "Best Time", value: "May-Oct" },
    { label: "From Karimabad", value: "~45 min" },
  ],
  nearby: [
    { name: "Passu Cones", dist: "30 km north", image: "/images/licensed/pasu-cones.jpg" },
    { name: "Baltit Fort", dist: "In Karimabad", image: "/images/licensed/baltit-fort-hunza-valley.jpg" },
    { name: "Hussaini Bridge", dist: "25 km north", image: "/images/licensed/the-bridge-of-shepherds-hussaini-suspension-bridge.jpg" },
  ],
};

export const HUNZA_AREA = {
  name: "Hunza Valley",
  region: "Gilgit-Baltistan",
  heroImage: "/images/hunza-valley-rakaposhi.webp",
  tagline: "The jewel of the north, apricot orchards, thousand-year forts, and Rakaposhi filling the sky.",
  facts: [
    { label: "Region", value: "Gilgit-Baltistan" },
    { label: "Give It", value: "3-4 days" },
    { label: "Altitude", value: "~2,500m" },
    { label: "Best Time", value: "Apr-Oct" },
  ],
  overview: {
    title: "A Valley Out of a Story",
    paragraphs: [
      "Strung along the Hunza River beneath Rakaposhi and Ultar Sar, Hunza is the most beloved destination in the Pakistani north. Karimabad, the main town, climbs the hillside below the restored Baltit Fort; across the valley, Altit Fort guards the oldest settlement.",
      "Give it three or four days, for the forts, the Eagle's Nest sunrise, the boat across Attabad Lake, and a day trip up toward Passu and the Khunjerab Pass.",
    ],
  },
  mapImage: "/images/attabad-lake.jpg",
  sights: [
    { name: "Baltit Fort", blurb: "A 700-year-old fort above Karimabad, beautifully restored.", image: "/images/licensed/baltit-fort-hunza-valley.jpg", href: "/destinations/gilgit-baltistan" },
    { name: "Attabad Lake", blurb: "Turquoise water born of a landslide, best by boat.", image: "/images/attabad-lake.jpg", href: "/places/attabad-lake" },
    { name: "Eagle's Nest", blurb: "The valley's classic sunrise viewpoint over Rakaposhi.", image: "/images/attractions/rakaposhi.jpg", href: "/areas/hunza" },
    { name: "Altit Fort & Village", blurb: "The oldest settlement in Hunza and its royal garden.", image: "/images/licensed/altit-fort-gb.jpg", href: "/areas/hunza" },
    { name: "Passu Cones", blurb: "The jagged 'cathedral' peaks up the Karakoram Highway.", image: "/images/licensed/pasu-cones.jpg", href: "/places/khunjerab" },
    { name: "Hopper Glacier", blurb: "A short drive to a dramatic, accessible glacier snout.", image: "/images/hopar-valley-glacier.jpg", href: "/places/naltar-valley" },
  ],
  /** Deliberately empty since 2026-09-20. This held three "stays", two of
   *  which GreenPak does not own — "Eagle's Nest Lodge" and "Karimabad
   *  Orchard Inn" — with invented nightly rates and star ratings, and they
   *  reached the live Hunza area guide. Resorts now come from the catalogue
   *  (see areaStayCards in load-areas.ts). Do not repopulate this with
   *  example properties. */
  stays: [] as { name: string; rating: string; price: string; image: string; href: string }[],
};
